import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import useApi from '../api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { X } from 'lucide-react';
import { DirectoryUser, JOB_MODE_OPTIONS, Job, JobMode, Service, apiErrorMessage } from '../services/servicesShared';
import { DirectorySearch, directoryUserLabel } from './DirectorySearch';

type Who = 'one' | 'many' | 'everyone';

interface DryRun {
    candidates: number;
    would_create: number;
    would_reactivate: number;
    already_on: number;
    ambiguous_workspace: string[];
    truncated: boolean;
}

/**
 * Vendor assigns a job (POST /services/{id}/jobs, or /jobs/bulk for many):
 * one customer, a picked set, or everyone who has identified. One job per
 * (service, subject); assigning again reactivates the existing row. The
 * mode (live | shadow) is chosen here and defaults to the service's.
 */
export const AssignJobDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    service: Pick<Service, 'id' | 'name' | 'scope' | 'published_version_number' | 'status' | 'default_job_mode'> | null;
    /** Offered when the dialog opens from the org-wide list. */
    services?: Service[];
    onAssigned: (job: Job & { created?: boolean }) => void;
    /** Called after a bulk assignment so the list can refresh. */
    onBulkAssigned?: () => void;
}> = ({ open, onClose, service: initialService, services, onAssigned, onBulkAssigned }) => {
    const { post } = useApi();
    const [serviceId, setServiceId] = useState<string>(initialService?.id || '');
    const [who, setWho] = useState<Who>('one');
    const [user, setUser] = useState<DirectoryUser | null>(null);
    const [tenantId, setTenantId] = useState<string>('');
    const [picked, setPicked] = useState<DirectoryUser[]>([]);
    const [mode, setMode] = useState<JobMode | ''>('');
    const [dryRun, setDryRun] = useState<DryRun | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setServiceId(initialService?.id || '');
            setWho('one');
            setUser(null);
            setTenantId('');
            setPicked([]);
            setMode('');
            setDryRun(null);
        }
    }, [open, initialService?.id]);

    const service = initialService || services?.find((item) => item.id === serviceId) || null;
    const assignable = (services || []).filter((item) => item.status === 'active' && item.published_version_number != null);
    const effectiveMode: JobMode = mode || service?.default_job_mode || 'live';

    useEffect(() => {
        // Default to the only workspace; otherwise make the person choose.
        if (user?.tenants.length === 1) setTenantId(user.tenants[0].id);
        else setTenantId('');
    }, [user]);

    // Everyone: preview the counts before anything is created.
    useEffect(() => {
        if (!open || who !== 'everyone' || !service) {
            setDryRun(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const response = await post(`/services/${service.id}/jobs/bulk`, { everyone: true, dry_run: true, mode: effectiveMode });
                if (!cancelled) setDryRun(response.data);
            } catch (error) {
                if (!cancelled) toast.error(apiErrorMessage(error, 'Could not count customers'));
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, who, service?.id, effectiveMode]);

    const needsTenant = who === 'one' && service?.scope === 'tenant' && Boolean(user?.tenants.length);
    const canSubmit = useMemo(() => {
        if (!service || saving) return false;
        if (who === 'one') return Boolean(user) && (!needsTenant || Boolean(tenantId));
        if (who === 'many') return picked.length > 0;
        return Boolean(dryRun) && (dryRun!.would_create > 0 || dryRun!.would_reactivate > 0);
    }, [service, saving, who, user, needsTenant, tenantId, picked.length, dryRun]);

    const submit = async () => {
        if (!service) return;
        setSaving(true);
        try {
            if (who === 'one') {
                if (!user) return;
                const response = await post(`/services/${service.id}/jobs`, {
                    customer_user_id: user.id,
                    ...(tenantId ? { customer_tenant_id: tenantId } : {}),
                    mode: effectiveMode,
                });
                toast.success(response.data.created ? (effectiveMode === 'shadow' ? 'Job started in shadow mode' : 'Job started') : 'Job reactivated (it already existed)');
                onAssigned(response.data);
                onClose();
                return;
            }
            const body =
                who === 'everyone'
                    ? { everyone: true, mode: effectiveMode }
                    : {
                          customer_user_ids: picked.map((item) => item.id),
                          tenant_by_user: Object.fromEntries(picked.filter((item) => item.tenants.length === 1).map((item) => [item.id, item.tenants[0].id])),
                          mode: effectiveMode,
                      };
            const response = await post(`/services/${service.id}/jobs/bulk`, body);
            const summary = response.data?.summary || {};
            const parts = [
                summary.created ? `${summary.created} started` : null,
                summary.reactivated ? `${summary.reactivated} reactivated` : null,
                summary.already_on ? `${summary.already_on} already on` : null,
                summary.skipped ? `${summary.skipped} skipped` : null,
                summary.ambiguous_workspace?.length ? `${summary.ambiguous_workspace.length} need a workspace picked` : null,
            ].filter(Boolean);
            toast.success(`Jobs: ${parts.join(', ') || 'nothing to do'}${effectiveMode === 'shadow' ? ' (shadow mode)' : ''}`);
            onBulkAssigned?.();
            onClose();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Could not assign the job'));
        } finally {
            setSaving(false);
        }
    };

    const submitLabel = () => {
        if (saving) return 'Starting…';
        if (who === 'everyone') {
            const n = (dryRun?.would_create || 0) + (dryRun?.would_reactivate || 0);
            return n ? `Start ${n} job${n === 1 ? '' : 's'}` : 'Start jobs';
        }
        if (who === 'many') return picked.length > 1 ? `Start ${picked.length} jobs` : 'Start job';
        return 'Start job';
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Assign a job{service ? ` — ${service.name}` : ''}</DialogTitle>
                    <DialogDescription>
                        Each job starts right away with a setup wake and keeps working for its customer until it is turned off.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    {!initialService && (
                        <div className="space-y-1">
                            <Label className="text-sm">Service</Label>
                            <Select value={serviceId} onValueChange={setServiceId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Choose a published service…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {assignable.map((item) => (
                                        <SelectItem key={item.id} value={item.id}>
                                            {item.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {!assignable.length && <p className="text-xs text-muted-foreground">Publish a service first.</p>}
                        </div>
                    )}

                    <div className="space-y-1">
                        <Label className="text-sm">Who</Label>
                        <div className="grid grid-cols-3 gap-1 rounded-md border p-1">
                            {(
                                [
                                    ['one', 'One customer'],
                                    ['many', 'Pick several'],
                                    ['everyone', 'Everyone'],
                                ] as [Who, string][]
                            ).map(([value, label]) => (
                                <button
                                    key={value}
                                    type="button"
                                    className={`rounded px-2 py-1 text-sm ${who === value ? 'bg-muted font-medium' : 'text-muted-foreground hover:bg-muted/60'}`}
                                    onClick={() => setWho(value)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {who === 'one' && (
                        <>
                            <div className="space-y-1">
                                <Label className="text-sm">
                                    Customer<span className="text-red-500 ml-0.5">*</span>
                                </Label>
                                <DirectorySearch selected={user} onSelect={setUser} autoFocus />
                                <p className="text-xs text-muted-foreground">
                                    {service?.scope === 'tenant'
                                        ? 'This person is the customer contact: accountable for the job and the identity it acts as.'
                                        : 'The job is about this person.'}
                                </p>
                            </div>
                            {user && service?.scope === 'tenant' && (
                                <div className="space-y-1">
                                    <Label className="text-sm">Workspace</Label>
                                    {user.tenants.length ? (
                                        <Select value={tenantId} onValueChange={setTenantId}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Choose a workspace…" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {user.tenants.map((tenant) => (
                                                    <SelectItem key={tenant.id} value={tenant.id}>
                                                        {tenant.name || tenant.external_id || tenant.id}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <p className="text-xs text-amber-600 dark:text-amber-400">
                                            This person belongs to no workspace, so the job will be scoped to them personally.
                                        </p>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {who === 'many' && (
                        <div className="space-y-2">
                            <Label className="text-sm">
                                Customers<span className="text-red-500 ml-0.5">*</span>
                            </Label>
                            {picked.length > 0 && (
                                <ul className="flex flex-wrap gap-1">
                                    {picked.map((item) => (
                                        <li key={item.id} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs">
                                            {directoryUserLabel(item)}
                                            <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setPicked((prev) => prev.filter((p) => p.id !== item.id))} aria-label="Remove">
                                                <X className="h-3 w-3" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <DirectorySearch
                                selected={null}
                                onSelect={(next) => {
                                    if (next && !picked.some((p) => p.id === next.id)) setPicked((prev) => [...prev, next]);
                                }}
                                autoFocus
                            />
                            <p className="text-xs text-muted-foreground">
                                {service?.scope === 'tenant'
                                    ? 'Each person becomes the customer contact for their workspace. People in several workspaces are skipped here; assign those one at a time.'
                                    : 'One job per person.'}
                            </p>
                        </div>
                    )}

                    {who === 'everyone' && (
                        <div className="rounded-md border px-3 py-2 text-sm">
                            {dryRun ? (
                                <>
                                    <div>
                                        {dryRun.candidates.toLocaleString()} identified customer{dryRun.candidates === 1 ? '' : 's'}
                                        {dryRun.truncated ? ' (first 500)' : ''}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                        {dryRun.would_create} new job{dryRun.would_create === 1 ? '' : 's'}
                                        {dryRun.would_reactivate ? `, ${dryRun.would_reactivate} reactivated` : ''}
                                        {dryRun.already_on ? `, ${dryRun.already_on} already on` : ''}
                                        {dryRun.ambiguous_workspace.length ? `, ${dryRun.ambiguous_workspace.length} skipped (several workspaces)` : ''}
                                    </div>
                                </>
                            ) : (
                                <span className="text-muted-foreground">Counting…</span>
                            )}
                        </div>
                    )}

                    <div className="space-y-1">
                        <Label className="text-sm">Mode</Label>
                        <Select value={effectiveMode} onValueChange={(value) => setMode(value as JobMode)}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {JOB_MODE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">{JOB_MODE_OPTIONS.find((option) => option.value === effectiveMode)?.description}</p>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={!canSubmit}>
                        {submitLabel()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default AssignJobDialog;
